import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import {  useNavigate, useParams } from 'react-router-dom';
import api from '~/services/api';
import dates from '~/utils/dates';
import './styles.css';

// // Contexts
import { GeneralContext } from '~/contexts';
import checkProviders from '~/utils/checkProviders';
import { MixPanelContext } from '~/contexts/mixPanel';

// Types kept minimal and aligned with current usage in Report UI
type ProviderInfo = { id?: number; name?: string };
type ReportSharedData = {
	id?: number;
	name?: string;
	init?: any;
	agent?: any;
	profile?: any;
	meter?: any;
	sdpid?: any;
	client_id?: any;
	client?: any;
	provider?: ProviderInfo;
	type?: 'inc' | string;
	power?: any;
	modality?: any;
};
type CativeCalc = { totalFlag: number };
type FreeCalc = { total: number };
export type UnitCalculate = {
	data: ReportSharedData;
	cativo: CativeCalc;
	mercadoLivre: FreeCalc;
};

function toNumber(val: any, fallback = 0): number {
	const n = typeof val === 'string' ? parseFloat(val) : (typeof val === 'number' ? val : NaN);
	return Number.isFinite(n) ? Number(n) : fallback;
}

function toNumberOrNull(val: any): number | null {
	const n = typeof val === 'string' ? parseFloat(val) : (typeof val === 'number' ? val : NaN);
	return Number.isFinite(n) ? Number(n) : null;
}

export const useSingleReport = (reportType: 'definitive' | 'preliminary', month: number, year: number) => {
	const {user, unit}   = useContext(GeneralContext);
    const {buttonClick}   = useContext(MixPanelContext);

	const navigate       = useNavigate();
	const checkProvider  = checkProviders.checkById(unit?.provider?.id);
	let   params         = useParams();
	let   yearMonth: any = params.yearMonth;

    // Dates
    const months         = dates.getMonthsYear(true);
    const monthCurr      = dates.getCurrMonthAPI();

    // States
    const [ready, setReady]               = useState<boolean>(false);
    const [unitCalc, setUnitCalculate]    = useState<UnitCalculate | undefined | {}>();
    const [statusReq, setStatusReq]    = useState<any>();
    const [downReady, setDownReady]       = useState<boolean>(true);
    const [downPDFReady, setDownPDFReady] = useState<boolean>(true);
    const [typeReport, setTypeReport]     = useState<string>('preliminary');

    // Selected Year/Month:
    // - If route param `:yearMonth` exists (e.g. "2026-01"), use it
    // - Otherwise fallback to the `month/year` passed into the hook (e.g. from navigation state)
    const ymSel = useMemo<{ y: number; m: number }>(() => {
        if (typeof yearMonth === 'string' && yearMonth.includes('-')) {
            const [yy, mm] = yearMonth.split('-');
            const y = Number(yy);
            const m = Number(mm);
            if (Number.isFinite(y) && Number.isFinite(m) && m >= 1 && m <= 12) return { y, m };
        }
        return { y: Number(year), m: Number(month) };
    }, [yearMonth, year, month]);

    const ymForExport = useMemo(() => {
        const y = ymSel.y;
        const m = ymSel.m;
        if (!Number.isFinite(y) || !Number.isFinite(m) || m < 1 || m > 12) return null;
        return `${String(y)}-${String(m).padStart(2, '0')}`;
    }, [ymSel]);

    // Functions
    function exportExcel() {
        setDownReady(false);
        console.log('exportExcel', unit.id, ymForExport);

        const isPreliminary = typeReport === 'preliminary';
        const url = `export/report/excel/${unit.id}${ ymForExport ? `/${ymForExport}` : ''}`;

        api.get(url, {
            headers: { Authorization: user.token },
            responseType: 'blob',
            // Backend can export preliminary via query param in the same route
            params: isPreliminary ? { type: 'preliminary' } : undefined,
        }).then((resp: any) => {
            const contentType: string = String(resp?.headers?.['content-type'] || resp?.data?.type || '');
            const isLikelyErrorPayload = /application\/json|text\/html/i.test(contentType);

            // If backend returns JSON/HTML (common for auth/500 errors), don't save it as .xlsx.
            if (isLikelyErrorPayload && resp?.data instanceof Blob) {
                resp.data.text().then((txt: string) => {
                    try {
                        const parsed = JSON.parse(txt);
                        alert(parsed?.message || parsed?.error || 'Falha ao exportar Excel (resposta inválida do servidor).');
                    } catch {
                        alert('Falha ao exportar Excel (servidor retornou erro ao invés do arquivo).');
                    }
                }).finally(() => setDownReady(true));
                return;
            }

            const excelMime = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
            const blob: Blob = resp?.data instanceof Blob ? resp.data : new Blob([resp.data], { type: excelMime });
            const normalizedBlob = blob.type ? blob : new Blob([blob], { type: excelMime });
            let url      = window.URL.createObjectURL(normalizedBlob);
            let link     = document.createElement('a');
            const typeLabel = isPreliminary ? 'PRELIMINAR' : 'DEFINITIVO';
            let fileName = `Relatório ${typeLabel} Cativo x Mercado Livre - ${ymForExport ? (dates._getNameMonth(ymSel.m-1) + ' de ' + ymSel.y) : (dates._getNameMonth(monthCurr.month-1) + ' de ' + monthCurr.year)} - ${unit.name}.xlsx`;

            link.href = url;
            link.setAttribute("id", "download");
            link.setAttribute('download', fileName); //or any other extension
            document.body.appendChild(link);
            link.click();
            link.parentNode?.removeChild(link);
            window.URL.revokeObjectURL(url);
            setDownReady(true);
            buttonClick(user.id, 'Relatório', 'Export do relatório Excel completo')
        }).catch(err => {
            buttonClick(user.id, 'Relatório', 'Falha. Export do relatório Excel incompleto')
            console.log(err);
            if (isPreliminary && err?.response?.status === 404) {
                alert('Relatório preliminar não encontrado. Gere primeiro (POST /preliminary-reports) e tente novamente.');
            }
            setDownReady(true);
        });
    }

    function exportPDF() {
        setDownPDFReady(false);
        console.log('exportPDF', unit.id, ymForExport);

        const isPreliminary = typeReport === 'preliminary';
        const legacyUrl = `export/report/pdf/${unit.id}${ ymForExport ? `/${ymForExport}` : ''}`;
        const newUrl = `export/preliminary-report/pdf/${unit.id}/${ymSel.y}/${String(ymSel.m).padStart(2, '0')}`;

        const request = () => api.get(isPreliminary ? legacyUrl : legacyUrl, {
            headers: { Authorization: user.token },
            responseType: 'blob',
            // Backend supports preliminary export via query param on legacy route
            params: isPreliminary ? { type: 'preliminary' } : undefined,
        });

        request().then((resp: any) => {
            const contentType: string = String(resp?.headers?.['content-type'] || resp?.data?.type || '');
            const isLikelyErrorPayload = /application\/json|text\/html/i.test(contentType);

            if (isLikelyErrorPayload && resp?.data instanceof Blob) {
                resp.data.text().then((txt: string) => {
                    try {
                        const parsed = JSON.parse(txt);
                        alert(parsed?.message || parsed?.error || 'Falha ao exportar PDF (resposta inválida do servidor).');
                    } catch {
                        alert('Falha ao exportar PDF (servidor retornou erro ao invés do arquivo).');
                    }
                }).finally(() => setDownPDFReady(true));
                return;
            }

            const pdfBlob: Blob = resp?.data instanceof Blob ? resp.data : new Blob([resp.data], { type: 'application/pdf' });
            let url      = window.URL.createObjectURL(pdfBlob.type ? pdfBlob : new Blob([pdfBlob], { type: 'application/pdf' }));
            // IMPORTANT: don't revoke the blob URL here; the new tab's PDF viewer may still be reading it.
            window.open(url, '_blank');
            setDownPDFReady(true);
            buttonClick(user.id, 'Relatório', 'Export do relatório PDF completo')

        }).catch(err => {
            // If preliminary export via legacy route isn't supported, fallback to the dedicated route
            if (isPreliminary && err?.response?.status === 404) {
                api.get(newUrl, {
                    headers: { Authorization: user.token },
                    responseType: 'blob',
                }).then((resp2: any) => {
                    const contentType2: string = String(resp2?.headers?.['content-type'] || resp2?.data?.type || '');
                    const isLikelyErrorPayload2 = /application\/json|text\/html/i.test(contentType2);

                    if (isLikelyErrorPayload2 && resp2?.data instanceof Blob) {
                        resp2.data.text().then((txt: string) => {
                            try {
                                const parsed = JSON.parse(txt);
                                alert(parsed?.message || parsed?.error || 'Falha ao exportar PDF preliminar.');
                            } catch {
                                alert('Falha ao exportar PDF preliminar (servidor retornou erro).');
                            }
                        }).finally(() => setDownPDFReady(true));
                        return;
                    }

                    const pdfBlob2: Blob = resp2?.data instanceof Blob ? resp2.data : new Blob([resp2.data], { type: 'application/pdf' });
                    const url2 = window.URL.createObjectURL(pdfBlob2.type ? pdfBlob2 : new Blob([pdfBlob2], { type: 'application/pdf' }));
                    // IMPORTANT: don't revoke the blob URL here; the new tab's PDF viewer may still be reading it.
                    window.open(url2, '_blank');
                    setDownPDFReady(true);
                    buttonClick(user.id, 'Relatório', 'Export do relatório PDF preliminar')
                }).catch((err2: any) => {
                    console.log(err2);
                    alert('Relatório preliminar não encontrado. Gere primeiro (POST /preliminary-reports) e tente novamente.');
                    setDownPDFReady(true);
                });
                return;
            }

            buttonClick(user.id, 'Relatório', 'Falha. Export do relatório PDF incompleto')
            console.log(err);
            setDownPDFReady(true);
        });
    }

    const getReport = useCallback((month: number | string, year: number | string) => {
        const ym = `${String(year)}-${String(month)}`;
        const url = `clients/unit/report/${unit?.id}/${ym}`;
        api.get(url, {
            headers: { Authorization: user.token }
        }).then((resp: any) => {
                try {
                    if (resp?.data?.message === "Data not found.") {
                        alert('Nenhum dado encontrado para o relatório. Por favor, verifique os dados da unidade.');
                        navigate('/relatorio/geranciador-de-relatorios');
                        return;
                    }

                    const calculate = resp?.data?.calculate || {};
                    const calculateData = calculate?.data || {};
                    const rawMercadoLivreCalc = calculate?.mercadoLivre || calculate?.mercado_livre || resp?.data?.mercadoLivre || {};
                    const rawCativoCalc = calculate?.cativo || resp?.data?.cativo || {};

                    const normalizeNoIcmsAliases = (obj: any) => {
                        const demandPeakNo = toNumber(obj?.demandPeakNoICMS ?? obj?.demandPeakNoIcms ?? obj?.demandPeakNoIcmsValue ?? obj?.demandPeakNoICMSValue);
                        const demandOffPeakNo = toNumber(obj?.demandOffPeakNoICMS ?? obj?.demandOffPeakNoIcms ?? obj?.demandOffPeakNoIcmsValue ?? obj?.demandOffPeakNoICMSValue);
                        const demandUniqueNo = toNumber(obj?.demandNoIcmsUnique ?? obj?.demandNoICMSUnique ?? obj?.demandNoIcmsUniqueValue ?? obj?.demandNoICMSUniqueValue);
                        return {
                            ...obj,
                            demandPeakNoICMS: demandPeakNo,
                            demandPeakNoIcms: demandPeakNo,
                            demandOffPeakNoICMS: demandOffPeakNo,
                            demandOffPeakNoIcms: demandOffPeakNo,
                            demandNoIcmsUnique: demandUniqueNo,
                            demandNoICMSUnique: demandUniqueNo,
                        };
                    };

                    const normalizeMercadoLivre = (obj: any) => {
                        // NOTE:
                        // - UI (`MercadoLivreBox`) reads teAclAmount/teAclTariff/teAclValue
                        // - Notes (`treatNotes`) reads teACL/perdaTrans/proinfa
                        // Definitive payload often comes with teACL/perdaTrans/proinfa (values) but without the camelCase trio.
                        const teAclAmount = toNumberOrNull(obj?.teAclAmount ?? obj?.te_acl_amount);
                        const teAclTariff = toNumberOrNull(obj?.teAclTariff ?? obj?.teACLTariff ?? obj?.te_acl_tariff);
                        const teAclValue = toNumberOrNull(obj?.teAclValue ?? obj?.teACL ?? obj?.teACLValue ?? obj?.te_acl_value);

                        const perdaTransAmount = toNumberOrNull(obj?.perdaTransAmount ?? obj?.perdaTransQtty ?? obj?.perdaTransQuantity ?? obj?.perda_trans_amount);
                        const perdaTransTariff = toNumberOrNull(obj?.perdaTransTariff ?? obj?.perdaTransMult ?? obj?.perda_trans_tariff);
                        const perdaTransValue = toNumberOrNull(obj?.perdaTransValue ?? obj?.perdaTrans ?? obj?.perda_trans_value);

                        const proinfaAmount = toNumberOrNull(obj?.proinfaAmount ?? obj?.proinfaQtty ?? obj?.proinfaQuantity ?? obj?.proinfa_energy_amount);
                        const proinfaTariff = toNumberOrNull(obj?.proinfaTariff ?? obj?.proinfa_energy_tariff);
                        const proinfaValue = toNumberOrNull(obj?.proinfaValue ?? obj?.proinfa ?? obj?.proinfa_energy_value);

                        // Keep both naming styles: UI uses camelCase; treatNotes expects teACL/perdaTrans/proinfa (values).
                        const normalized = {
                            ...obj,
                            teAclAmount,
                            teAclTariff,
                            teAclValue,
                            teACL: teAclValue ?? toNumberOrNull(obj?.teACL) ?? null,
                            perdaTransAmount,
                            perdaTransTariff,
                            perdaTransValue,
                            perdaTrans: perdaTransValue ?? toNumberOrNull(obj?.perdaTrans) ?? null,
                            proinfaAmount,
                            proinfaTariff,
                            proinfaValue,
                            proinfa: proinfaValue ?? toNumberOrNull(obj?.proinfa) ?? null,
                        };
                        return normalizeNoIcmsAliases(normalized);
                    };

                    let mercadoLivreCalc = normalizeMercadoLivre(rawMercadoLivreCalc);
                    const cativoCalc = normalizeNoIcmsAliases(rawCativoCalc);

                    const cceeFee = mercadoLivreCalc?.ccee;
                    const cceeErcapFee = mercadoLivreCalc?.ccee_ercap;
                    const backupPower = mercadoLivreCalc?.bkpPower;
                    const financialSettlement = mercadoLivreCalc?.liqFinc;
                    const nuclearEnergy = mercadoLivreCalc?.nuclearEnergy;

                    const hasPendingChargeData = [
                        cceeFee,
                        cceeErcapFee,
                    ].some((value) => value === 0);

                    const hasPendingChargeDataNotNull = [
                        cceeFee,
                        cceeErcapFee,
                        backupPower,
                        financialSettlement,
                        nuclearEnergy,
                    ].some((value) => value === null || value === undefined);

                    const isWholesaleUnit = resp?.data?.client?.unit_type === 'wholesaler';

                    if (hasPendingChargeData && isWholesaleUnit && hasPendingChargeDataNotNull) {
                        alert('Dados pendentes (CCEE, CCEE_ERCAP, BKP_POWER, LIQ_FINC) para gerar o relatório. Por favor, verifique os dados da unidade.');
                        navigate('/relatorio/geranciador-de-relatorios');
                        return;
                    }

                    // Normalize `data` shape so UI components don't crash when backend omits nested objects.
                    const normalizedData: any = {
                        ...calculateData,
                        demands: calculateData?.demands ?? {
                            contracted: 0,
                            consumed: { peak: 0, offPeak: 0 },
                            notConsumed: { peak: 0, offPeak: 0 },
                            overtaking: { peak: 0, offPeak: 0 },
                        },
                        others: calculateData?.others ?? {
                            year: toNumber(calculateData?.year),
                            month: toNumber(calculateData?.month),
                            cative: 0,
                            freeMarket: 0,
                            notes: '',
                        },
                        consumptionPeak: toNumber(calculateData?.consumptionPeak),
                        consumptionOffPeak: toNumber(calculateData?.consumptionOffPeak),
                        consumptionTE: toNumber(calculateData?.consumptionTE),
                        consumptionPeakTE: toNumber(calculateData?.consumptionPeakTE),
                        consumptionOffPeakTE: toNumber(calculateData?.consumptionOffPeakTE),
                        modality: calculateData?.modality ?? calculateData?.provider?.modality ?? 'Verde',
                        pisCofins: toNumber(calculateData?.pisCofins),
                        icms: toNumber(calculateData?.icms),
                        type: calculateData?.type ?? 'inc',
                    };

                    // Infer missing "TE ACL / Perdas / Proinfa" fields in definitive payload so the UI can show price/value.
                    // Definitive commonly has:
                    // - `data.price` (R$/kWh)
                    // - `data.consumptionTE` (kWh)
                    // - `mercadoLivre.teACL` / `mercadoLivre.perdaTrans` / `mercadoLivre.proinfa` (values in R$)
                    const inferredPrice = toNumberOrNull(
                        normalizedData?.price ??
                        normalizedData?.price_billing_value ??
                        (calculateData as any)?.priceBillingValue ??
                        (calculateData as any)?.priceBilling ??
                        null
                    );
                    const inferredConsumptionTE = toNumberOrNull(normalizedData?.consumptionTE);

                    const inferredTeAclValue = toNumberOrNull(mercadoLivreCalc?.teAclValue ?? mercadoLivreCalc?.teACL ?? (rawMercadoLivreCalc as any)?.teACL);
                    const inferredTeAclAmount = toNumberOrNull(mercadoLivreCalc?.teAclAmount ?? inferredConsumptionTE);
                    const inferredTeAclTariff = toNumberOrNull(mercadoLivreCalc?.teAclTariff ?? inferredPrice);

                    const inferredPerdaTransValue = toNumberOrNull(mercadoLivreCalc?.perdaTransValue ?? mercadoLivreCalc?.perdaTrans ?? (rawMercadoLivreCalc as any)?.perdaTrans);
                    const inferredPerdaTransAmount = toNumberOrNull(mercadoLivreCalc?.perdaTransAmount ?? (rawMercadoLivreCalc as any)?.perdaTransQtty ?? inferredConsumptionTE);
                    const inferredPerdaTransTariff = toNumberOrNull(mercadoLivreCalc?.perdaTransTariff ?? inferredPrice);

                    const inferredProinfaValue = toNumberOrNull(mercadoLivreCalc?.proinfaValue ?? mercadoLivreCalc?.proinfa ?? (rawMercadoLivreCalc as any)?.proinfa);
                    const inferredProinfaAmount = toNumberOrNull(mercadoLivreCalc?.proinfaAmount ?? normalizedData?.proinfa);
                    const inferredProinfaTariff = toNumberOrNull(mercadoLivreCalc?.proinfaTariff ?? inferredPrice);

                    mercadoLivreCalc = {
                        ...mercadoLivreCalc,
                        // TE ACL
                        teAclAmount: inferredTeAclAmount,
                        teAclTariff: inferredTeAclTariff,
                        teAclValue: inferredTeAclValue ?? (Number.isFinite(inferredTeAclAmount as any) && Number.isFinite(inferredTeAclTariff as any)
                            ? Number(inferredTeAclAmount) * Number(inferredTeAclTariff)
                            : null),
                        teACL: inferredTeAclValue ?? mercadoLivreCalc?.teACL ?? null,
                        // Perdas de transmissão
                        perdaTransAmount: inferredPerdaTransAmount,
                        perdaTransTariff: inferredPerdaTransTariff,
                        perdaTransValue: inferredPerdaTransValue,
                        perdaTrans: inferredPerdaTransValue ?? mercadoLivreCalc?.perdaTrans ?? null,
                        // Proinfa
                        proinfaAmount: inferredProinfaAmount,
                        proinfaTariff: inferredProinfaTariff,
                        proinfaValue: inferredProinfaValue,
                        proinfa: inferredProinfaValue ?? mercadoLivreCalc?.proinfa ?? null,
                    };

                    setUnitCalculate({
                        data: normalizedData,
                        cativo: cativoCalc,
                        mercadoLivre: mercadoLivreCalc,
                    });
                    setStatusReq(resp?.status);
                    setTypeReport('definitive');
                    setReady(true);
                } catch (e) {
                    console.log('Erro ao processar relatório definitivo:', e);
                    setTypeReport('definitive');
                    setUnitCalculate({});
                    setStatusReq(resp?.status || 500);
                    setReady(true);
                }
        }).catch((err: any) => {
                setTypeReport('definitive');
                setUnitCalculate({});
                setStatusReq(err?.response?.status || 500);
                setReady(true);
            });
    }, [unit?.id, user.token, navigate]);

    const getReportPreliminary = useCallback((month: number | string, year: number | string) => {
            const prelimUrl = `preliminary-reports/${unit?.id}/${String(year)}/${String(month)}`;

            api.get(prelimUrl, {
                headers: { Authorization: user.token }
            }).then((resp: any) => {
                // Adapt preliminary response to the calculate shape expected by the UI
                const prelim = resp?.data?.data;
                setTypeReport('preliminary');

                const free = prelim?.preliminaryReportFree || {};
                const captive = prelim?.preliminaryReportCaptive || {};
                const inputData = prelim?.inputData || {};

                // Compose shared data object based on context unit and preliminary values
                const unitProvider = unit?.provider;
                const data: ReportSharedData = {
                    id: unit?.id,
                    name: unit?.name,
                    init: unit?.init_contract,
                    agent: unit?.agent,
                    profile: unit?.profile,
                    meter: unit?.meter,
                    sdpid: unit?.sdpid ?? null,
                    client_id: unit?.client_id ?? null,
                    client: unit?.client ?? '',
                    provider: unitProvider ? { id: unitProvider.id, name: unitProvider.provider?.name } : undefined,
                    type: unit?.type ?? 'inc',
                    power: unit?.provider?.power,
                    modality: unit?.provider?.modality ?? 'Verde',
                    price: toNumber(free?.priceBillingValue || free?.teAclTariff),
                    price_info: {
                        year: toNumber(free?.year || captive?.year, monthCurr.year),
                        month: toNumber(free?.month || captive?.month, monthCurr.month),
                    },
                    demands: {
                        contracted: toNumber(captive?.contractedDemandOffPeakAmount || free?.contractedDemandOffPeakAmount),
                        consumed: {
                            peak: toNumber(captive?.contractedDemandPeakAmount || free?.contractedDemandPeakAmount),
                            offPeak: toNumber(captive?.contractedDemandOffPeakAmount || free?.contractedDemandOffPeakAmount),
                        },
                        notConsumed: { peak: 0, offPeak: 0 },
                        overtaking: { peak: 0, offPeak: 0 },
                    },
                    consumptionPeak: toNumber(captive?.tusdPeakAmount || free?.tusdPeakAmount),
                    consumptionOffPeak: toNumber(captive?.tusdOffPeakAmount || free?.tusdOffPeakAmount),
                    others: { year: toNumber(free?.year || captive?.year), month: toNumber(free?.month || captive?.month), cative: 0, freeMarket: 0, notes: '' },
                    feeInterestCorrection: 0,
                    discounts: 0,
                    pisCofins: toNumber(free?.pisCofinsTariff || captive?.pisCofinsTariff) * 100, // convert fraction to % if needed
                    ccee: toNumber(free?.cceeFeeValue),
                    ccee_info: { year: 0, month: 0 },
                    ccee_ercap: toNumber(inputData?.ercapValue),
                    ccee_ercap_info: { year: 0, month: 0 },
                    proinfa: toNumber(free?.proinfaEnergyAmount),
                    proinfa_info: { year: toNumber(free?.year || captive?.year), month: toNumber(free?.month || captive?.month) },
                    lostEnergy: toNumber(free?.transmissionLossesAmount),
                    lostEnergy_info: { year: toNumber(free?.year || captive?.year), month: 0 },
                    covid: 0,
                    icms: toNumber(free?.icmsTariff || captive?.icmsTariff) * 100, // convert fraction to % if needed
                    year: toNumber(free?.year || captive?.year),
                    month: toNumber(free?.month || captive?.month),
                    consumptionTE: toNumber(captive?.tePeakAmount) + toNumber(captive?.teOffPeakAmount),
                    consumptionPeakTE: toNumber(captive?.tePeakAmount),
                    consumptionOffPeakTE: toNumber(captive?.teOffPeakAmount),
                    liquid: null,
                    liquidPerc: 0,
                    liquidPre: false,
                    nuclearEnergy: toNumber(inputData?.nuclearEnergyValue),
                    bkpPower: toNumber(inputData?.reserveEnergyValue),
                    teAcl: toNumberOrNull(free?.teAclAmount),
                } as any;

                const cativo: CativeCalc & Record<string, any> = {
                    demandPeak: toNumber(captive?.contractedDemandPeakValue),
                    demandOffPeak: toNumber(captive?.contractedDemandOffPeakValue),
                    // If backend provides these fields, use them; otherwise default to 0
                    demandPeakNoICMS: toNumber((captive as any)?.contractedDemandPeakNoIcmsValue ?? (captive as any)?.contractedDemandPeakNoICMSValue),
                    demandOffPeakNoICMS: toNumber((captive as any)?.contractedDemandOffPeakNoIcmsValue ?? (captive as any)?.contractedDemandOffPeakNoICMSValue),
                    demandOvertakingPeak: toNumber((captive as any)?.demandOvertakingPeakValue),
                    demandOvertakingOffPeak: toNumber((captive as any)?.demandOvertakingOffPeakValue),
                    demandUniqueMult: toNumber(captive?.contractedDemandOffPeakTariff),
                    demandOvertakingUniqueMult: toNumber((captive as any)?.demandOvertakingUniqueTariff),
                    demandPeakMult: toNumber(captive?.contractedDemandPeakTariff),
                    demandOffPeakMult: toNumber(captive?.contractedDemandOffPeakTariff),
                    demandOvertakingPeakMult: toNumber((captive as any)?.demandOvertakingPeakTariff),
                    demandOvertakingOffPeakMult: toNumber((captive as any)?.demandOvertakingOffPeakTariff),
                    consumptionPeakMult: toNumber(captive?.tusdPeakTariff),
                    consumptionOffPeakMult: toNumber(captive?.tusdOffPeakTariff),
                    tePeakMult: toNumber(captive?.tePeakTariff),
                    teOffPeakMult: toNumber(captive?.teOffPeakTariff),
                    consumptionPeak: toNumber(captive?.tusdPeakValue),
                    consumptionOffPeak: toNumber(captive?.tusdOffPeakValue),
                    tePeak: toNumber(captive?.tePeakValue),
                    teOffPeak: toNumber(captive?.teOffPeakValue),
                    demandUnique: toNumber(captive?.contractedDemandOffPeakValue),
                    demandNoIcmsUnique: toNumber((captive as any)?.contractedDemandOffPeakNoIcmsValue ?? (captive as any)?.contractedDemandOffPeakNoICMSValue),
                    demandOvertakingUnique: toNumber((captive as any)?.demandOvertakingUniqueValue),
                    calcIcms: toNumber(captive?.icmsCalculationBase),
                    calcPisCofins: toNumber(captive?.pisCofinsCalculationBase),
                    pisCofins: toNumber(captive?.pisCofinsValue),
                    icms: toNumber(captive?.icmsValue),
                    feeInterestCorrection: toNumber((captive as any)?.feeInterestCorrectionValue ?? (captive as any)?.feeInterestCorrection),
                    discounts: toNumber((captive as any)?.discountsValue ?? (captive as any)?.discounts),
                    total: toNumber(captive?.totalValue),
                    flagInfos: {
                        name: 'Informada',
                        value: toNumber(captive?.tariffFlagAmount),
                    },
                    flag: toNumber(captive?.tariffFlagValue),
                    totalFlag: toNumber(captive?.totalValueWithTariffFlag),
                } as any;

                const mercadoLivre: FreeCalc & Record<string, any> = {
                    demandPeak: toNumber(free?.contractedDemandPeakValue),
                    demandOffPeak: toNumber(free?.contractedDemandOffPeakValue),
                    demandPeakNoICMS: toNumber((free as any)?.contractedDemandPeakNoIcmsValue ?? (free as any)?.contractedDemandPeakNoICMSValue),
                    demandOffPeakNoICMS: toNumber((free as any)?.contractedDemandOffPeakNoIcmsValue ?? (free as any)?.contractedDemandOffPeakNoICMSValue),
                    demandOvertakingPeak: toNumber((free as any)?.demandOvertakingPeakValue),
                    demandOvertakingOffPeak: toNumber((free as any)?.demandOvertakingOffPeakValue),
                    demandUniqueMult: toNumber(free?.contractedDemandOffPeakTariff),
                    demandOvertakingUniqueMult: toNumber((free as any)?.demandOvertakingUniqueTariff),
                    // For "Azul" modality, the UI reads these fields for demand tariffs
                    demandPeakMult: toNumber(free?.contractedDemandPeakTariff),
                    demandOffPeakMult: toNumber(free?.contractedDemandOffPeakTariff),
                    demandOvertakingPeakMult: toNumber((free as any)?.demandOvertakingPeakTariff),
                    demandOvertakingOffPeakMult: toNumber((free as any)?.demandOvertakingOffPeakTariff),
                    consumptionPeakMult: toNumber(free?.tusdPeakTariff),
                    consumptionOffPeakMult: toNumber(free?.tusdOffPeakTariff),
                    tePeakMult: toNumber((free as any)?.tePeakTariff),
                    teOffPeakMult: toNumber((free as any)?.teOffPeakTariff),
                    consumptionPeak: toNumber(free?.tusdPeakValue),
                    consumptionOffPeak: toNumber(free?.tusdOffPeakValue),
                    demandUnique: toNumber(free?.contractedDemandOffPeakValue),
                    demandNoIcmsUnique: toNumber((free as any)?.contractedDemandOffPeakNoIcmsValue ?? (free as any)?.contractedDemandOffPeakNoICMSValue),
                    demandOvertakingUnique: toNumber((free as any)?.demandOvertakingUniqueValue),
                    teACLValue: toNumberOrNull(free?.teAclValue),
                    teAclAmount: toNumberOrNull(free?.teAclAmount),
                    teAclTariff: toNumberOrNull(free?.teAclTariff),
                    teAclValue: toNumberOrNull(free?.teAclValue),
                    perdaTransAmount: toNumberOrNull(free?.transmissionLossesAmount),
                    perdaTransValue: toNumberOrNull(free?.transmissionLossesValue),
                    perdaTransTariff: toNumberOrNull(free?.transmissionLossesTariff),
                    proinfaValue: toNumberOrNull(free?.proinfaEnergyValue),
                    proinfaAmount: toNumberOrNull(free?.proinfaEnergyAmount),
                    proinfaTariff: toNumberOrNull(free?.proinfaEnergyTariff),
                    calcPisCofins: toNumber(free?.pisCofinsCalculationBase),
                    calcIcms: toNumber(free?.icmsCalculationBase),
                    pisCofins: toNumber(free?.pisCofinsValue),
                    icms: toNumber(free?.icmsValue),
                    liqFinc: toNumber(inputData?.financialSettlementValue),
                    feeInterestCorrection: toNumber((free as any)?.feeInterestCorrectionValue ?? (free as any)?.feeInterestCorrection),
                    discounts: toNumber((free as any)?.discountsValue ?? (free as any)?.discounts),
                    liqFincPrev: toNumber((free as any)?.financialSettlementPrevValue ?? (free as any)?.financialSettlementPreviousValue),
                    // Keep current UI behavior: when `bkpPower` is null, component uses `bkpPowerPrev`.
                    // Preliminary payload returns `reserveEnergyValue`, so store it in `bkpPowerPrev`.
                    bkpPower: toNumber(inputData?.reserveEnergyValue),
                    bkpPowerPrev: toNumber(free?.reserveEnergyValue),
                    ccee: toNumber(inputData?.cceeFeeValue),
                    ccee_ercap: toNumber(inputData?.ercapValue),
                    nuclearEnergy: toNumber(inputData?.nuclearEnergyValue),
                    total: toNumber(free?.totalValue),
                } as any;

                setUnitCalculate({ data, cativo, mercadoLivre });
                setStatusReq(200);
                setReady(true);
            }).catch((err: any) => {
                if (err?.response?.status===400) {
                    // No preliminary data found, redirect to manager
                    console.log('Nenhum dado preliminar encontrado, redirecionando para o gerenciador');
                    navigate('/relatorio/geranciador-de-relatorios');
                } else {
                    console.log(err?.response || err);
                    setUnitCalculate({});
                    setStatusReq(err?.response?.status || 500);
                    setReady(true);
                }
            });
    }, [unit, user.token, navigate, monthCurr.month, monthCurr.year]);

    useEffect(() => {
        if (typeReport && month && year) {
            if (reportType === 'definitive') {
                const ym = typeof yearMonth === 'string' ? yearMonth : '';
                const [y, m] = ym && ym.includes('-') ? ym.split('-') : [String(year), String(month)];
                getReport(m, y);
            }else{
            getReportPreliminary(month, year);
            }
        }
    }, [yearMonth, month, year, reportType, getReport, getReportPreliminary, typeReport]);

    return({
        ready,
        checkProvider,
        unit,
        unitCalc,
        downReady,
        downPDFReady,
        yearMonth,
        months,
        monthCurr,
        exportPDF,
        exportExcel,
        statusReq,
        typeReport,
    });
}